const uuid = require('uuid');
import React from 'react';
import PropTypes from 'prop-types';

const _ = {
    find:       require('lodash/find'),
    findIndex:  require('lodash/findIndex'),
    filter:     require('lodash/filter'),
    assign:     require('lodash/assign'),
    forEach:    require('lodash/forEach'),
    sortBy:     require('lodash/sortBy'),
    map:        require('lodash/map'),
    clone:      require('lodash/clone'),
    without:    require('lodash/without'),
    isEqual:    require('lodash/isEqual'),
};
import ReactFlow, { ReactFlowProvider, ArrowHeadType, Handle, Position, removeElements, addEdge } from 'react-flow-renderer';

const ShortCourseEditorActions = require('../../actions/ShortCourseEditorActions');
const ShortCourseEditorStore = require('../../stores/ShortCourseEditorStore');

import EditorCompletionNode from './EditorCompletionNode';
import EditorSkillNode from './EditorSkillNode';

const ShortCourseConstants = require('../../../../app/shared/ShortCourseConstants');

const AssetUtil = require('../../util/asset');

// these keys should match the values in ShortCourseConstants.NODE_TYPES
const nodeTypes = {
    'completion': EditorCompletionNode,
    'skill': EditorSkillNode,
};

const arrowHeadType = ArrowHeadType.ArrowClosed; // ArrowHeadType.Arrow; 
const arrowHeadColor = 'black';

// flow diagram tweak constants 
const graph_width = 1000;
const graph_height = 1000;
const horizontal_spacing = 10;
const vertical_spacing = 5;
const occ_grid_width = 110;
const occ_grid_height = 55;

// returns {x,y}
function getNodeDimensions(node) {
    return {x:200,y:50}; // TODO: refine this later
}
 
const propTypes = {
    editedDoc: PropTypes.object.isRequired,
};
const defaultProps = {};

class ShortCourseFlowDiagram extends React.Component {
    constructor(props) {
        super(props);
        
        let {elements,occ_map} = this.buildElements(props.editedDoc);

        this.state = { 
            elements, occ_map, 
            errorLines: [],
        };
        this.unsubscribes = [ShortCourseEditorStore.listen(this.onEditedDocChange)];
    }
    
    componentWillUnmount() {
        if (this.onSetNodePosition_timer) {
            clearTimeout(this.onSetNodePosition_timer);
        }
        if (this.onAddPrereq_timer) {
            clearTimeout(this.onAddPrereq_timer);
        }
        this.unsubscribes.forEach(u => u());
    }
    
    UNSAFE_componentWillReceiveProps(nextProps) {
        if (!_.isEqual(this.props.editedDoc,nextProps.editedDoc)) {
            let {elements,occ_map} = this.buildElements(nextProps.editedDoc);
            this.setState({elements,occ_map});
        }
    }
    
    onAddSkillNode=(storeState)=>{
        let id = storeState.action.newNodeId;
        let elements = this.state.elements.slice();
        let xn = _.find(elements,{id});
        if (xn) {
            console.log(`onAddSkillNode warning: node ${id} already exists in graph`);
            return; 
        }
        const doc = storeState.shortCourseDoc;
        let node = _.find(doc.nodes,{node_id: id});
        if (!node) {
            console.log(`onAddSkillNode error: node ${id} not found in doc`);
            return;
        }
        if (node.prereq_node_ids.length) {
            console.log(`onAddSkillNode warning: node ${id} already has prerequisites`); // handle this or is it an error?
            // continue with the rest
        }
        
        let d = getNodeDimensions(node);
        let occ_map = _.clone(this.state.occ_map);
        if (!node.position) {
            this.calcPositionForNode(node,d,occ_map,doc,true);
        }
        
        let data = {
            name: node.name || this.getNodeDefaultName(node),
            description: node.description,
            numTargetHandles: 1,
            img_src: node.img_src && AssetUtil.resolveAssetPath(node.img_src),
        };

        // add an element for the new skill node
        elements.push({
            id: node.node_id,
            type: ShortCourseEditorStore.determineNodeType(node),
            position: node.position,
            selectable: true,
            connectable: true,
            // targetPosition: 'left', // default is top according to https://reactflow.dev/docs/api/nodes/node-options/
            // sourcePosition: 'right', // default is bottom
            data
        });
        
        this.markNodePosition(node,occ_map);
        this.setState({elements,occ_map});
    } // onAddSkillNode
    
    onDeleteSkillNode=(storeState)=>{
        let id = storeState.action.node_id;
        let elements = _.filter(this.state.elements,(elem)=>{
            if ((elem.id === id) || (elem.source === id) || (elem.target === id)) {
                return false;
            }
            return true;
        });
        this.setState({elements});
    } // onDeleteSkillNode
    
    // add corresponding edge
    onAddPrereq=(storeState)=>{
        const doc = storeState.shortCourseDoc;
        let node_id = storeState.action.node_id;
        let prereq_id = storeState.action.prereq_id;
        let node = _.find(doc.nodes,{node_id});
        if (!node) {
            console.log(`onAddPrereq error: node_id ${node_id} not found in doc`);
            return;
        }
        if (!_.find(doc.nodes,{node_id:prereq_id})) {
            console.log(`onAddPrereq error: prereq_id ${prereq_id} not found in doc`);
            return;
        }
        let preNodes = this.getHandleSortedPrerequisites(node,doc);
        let elements = this.state.elements.slice();
        
        let updates = [];
        
        // update node handles
        let node_index = _.findIndex(elements, {id:node_id});
        if (node_index < 0) {
            console.log(`onAddPrereq error: node_id ${node_id} not found in graph`);
            return;
        }
        
        let elem = elements[node_index];
        let data = { ...elem.data, numTargetHandles:preNodes.length };
        elements[node_index] = { ...elem, data: data };

        // update edges
        _.forEach(preNodes,(sourceNode,pre_index)=>{
            let edge_id = `e:${sourceNode.node_id}:${node.node_id}`
            let targetHandle = `p${pre_index+1}`;
            if (sourceNode.node_id === prereq_id) {
                let edge = {
                    id: edge_id,
                    source: sourceNode.node_id,
                    target: node.node_id,
                    targetHandle,
                    type: 'straight',
                    arrowHeadType,
                };
                updates.push(edge);
                elements.push(edge);
            }
            else {
                let i = _.findIndex(elements,{id:edge_id});
                let elem = elements[i];
                if (!elem) {
                    console.log(`onAddPrereq warning: expected edge ${edge_id} not found in graph`); // unexpected but we will correct.
                    let edge = {
                        id: edge_id,
                        source: sourceNode.node_id,
                        target: node.node_id,
                        targetHandle: targetHandle,
                        type: 'straight',
                        arrowHeadType,
                    };
                    elements.push(edge);
                    updates.push(edge);
                }
                else if (elem.targetHandle !== targetHandle) {
                    elements[i] = { ...elem, targetHandle:targetHandle };
                    updates.push(elem);
                }
            }
        });
        
        // delete then add edges so the new handle is updated properly
        let elements_without_changed_edges = removeElements(elements,updates);
        this.setState({elements: elements_without_changed_edges},()=>{
            this.onAddPrereq_timer = setTimeout(()=>{
                delete this.onAddPrereq_timer;
                this.setState({elements});
            },1);
        });
    } // onAddPrereq
    
    // remove corresponding edge
    onRemovePrereq=(storeState)=>{
        let node_id = storeState.action.node_id;
        let prereq_id = storeState.action.prereq_id;
        let id = `e:${prereq_id}:${node_id}`
        let edge = _.find(this.state.elements,{id});
        if (!edge) {
            console.log(`onRemovePrereq warning: expected edge ${id} not found in graph`);
            return;
        }
        let elements = _.without(this.state.elements,edge);
        // TODO: update handles
        this.setState({elements});
    } // onRemovePrereq

    onSetNodeValue=(storeState)=>{
        let id = storeState.action.node_id;
        let key = storeState.action.key;
        let value = storeState.action.value;
        if ((key === 'name') || (key === 'img_src') || (key === 'description')) {
            let elements = this.state.elements.slice();
            let ni = _.findIndex(elements,{id});
            let elem = elements[ni];
            if (elem) {
                let data = {...elem.data};
                if (key === 'img_src') {
                    value = value && AssetUtil.resolveAssetPath(value);
                }
                data[key] = value;
                elements[ni] = {...elem, data: data };
                this.setState({elements});
            }
        }
    }
    
    // update the graph element position
    onSetNodePosition=(storeState)=>{
        let id = storeState.action.node_id;
        let position = storeState.action.position;
        // need to use setTimeout so this gets done outside of the event callback 
        this.onSetNodePosition_timer = setTimeout(()=>{
            delete this.onSetNodePosition_timer;
            const storeState = ShortCourseEditorStore.getState();
            if (!storeState) {
                return;
            }
            const doc = storeState.shortCourseDoc;
            const node = _.find(doc.nodes,{node_id:id});
            if (!node) {
                return;
            }
            let elements = this.state.elements.slice();
            let ni = _.findIndex(elements,{id});
            let elem = elements[ni];
            if (elem) {
                
                let occ_map = _.clone(this.state.occ_map);
                this.unmarkNodePosition(node,occ_map, elem.position);
                this.markNodePosition(node,occ_map);
                elements[ni] = {...elem, position: position };
                this.setState({elements,occ_map});
            }
        },1);
    }
    
    onEditedDocChange=(storeState)=>{
        if (storeState.action && storeState.action.name && !storeState.action.error)  {
            switch(storeState.action.name) {
                case 'addSkillNode':
                    return this.onAddSkillNode(storeState);
                case 'addPrereq':
                    return this.onAddPrereq(storeState);
                case 'removePrereq':
                    return this.onRemovePrereq(storeState);
                case 'setNodeValue':
                    return this.onSetNodeValue(storeState);
                case 'setNodePosition':
                    return this.onSetNodePosition(storeState);
                case 'deleteSkillNode':
                    return this.onDeleteSkillNode(storeState);
            }
        }
    }
    
    // return position of lowest sibling, or undefined (falsy)
    findLowestSiblingPosition=(searchNode,doc)=>{
        var low_pos,sibNode;
        _.forEach(doc.nodes,(node)=>{
            if (node.prereq_node_ids && (node.prereq_node_ids.indexOf(searchNode.node_id) >= 0)) {
                _.forEach(node.prereq_node_ids,(node_id)=>{
                    let sib = _.find(doc.nodes,{node_id});
                    if (sib && sib.position) {
                        if (!low_pos || low_pos.y < sib.position.y) {
                            low_pos = sib.position;
                            sibNode = node;
                        }
                    }
                });
                // return false; // uncomment if a node can only be a prerequisite for one other node
            }
        });
        if (sibNode) {
            console.log(`${searchNode.name} should be below ${sibNode.name}`);
        }
        return low_pos;
    }
    
    // return an array of at least one prerequisite node with a position, or undefined (falsy)
    findRightMostPrereqPosition=(searchNode,doc)=>{
        let rightNode, right_pos;
        _.forEach(searchNode.prereq_node_ids,(node_id)=>{
            let preNode = _.find(doc.nodes,{node_id});
            if (preNode && preNode.position) {
                if (!right_pos || (right_pos.x < preNode.position.x)) {
                    right_pos = preNode.position;
                    rightNode = preNode;
                }
            }
        });
        if (rightNode) {
            console.log(`${searchNode.name} should be right of ${rightNode.name}`);
        }
        return right_pos;
    }
    
    findLeftMostChildPosition=(searchNode,doc)=>{
        let left_pos,leftNode;
        _.forEach(doc.nodes,(node)=>{
            if (node.prereq_node_ids && (node.prereq_node_ids.indexOf(searchNode.node_id) >= 0)) {
                if (node && node.position) {
                    if (!left_pos || (left_pos.x > node.position.x)) {
                        left_pos = node.position;
                        leftNode = node;
                    }
                }
            }
        });
        if (leftNode) {
            console.log(`${searchNode.name} should be left of ${leftNode.name}`);
        }
        return left_pos;
    }

    
    // count the number of occupied coordinates.
    // p = {x,y} position, d = {x,y} dimensions, occ_map = object tracking occupancy
    countOccupancy=(p,d,occ_map)=>{
        if (!p) {
            console.log("countOccupancy called without position");
            return 0;
        }
        let gx = Math.round(p.x / occ_grid_width);
        let gy = Math.round(p.y / occ_grid_height);
        let y_arr = occ_map[gy];
        if (y_arr) {
            let x_arr = y_arr[gx];
            return x_arr ? x_arr.length : 0;
        }
        return 0;
    }
    
    ensureNodeIsInClearSpot=(node,d,occ_map)=>{
        let p = node.position;
        let occupancy = this.countOccupancy(node.position,d,occ_map);
        if (!occupancy) {
            return; // good enough
        }

        let test_p = node.position;
        for (var q=0; q < 4; q++) { // quadrant direction
            let dx = (q&1) ? occ_grid_width : -occ_grid_width;
            let dy = (q&2) ? occ_grid_height : -occ_grid_height;
            for (var pass=0; pass<100; ++pass) {
                if (pass & 1) {
                    test_p.x += dx;
                }
                else {
                    test_p.y += dy;
                }
                occupancy = this.countOccupancy(test_p,d,occ_map);
                if (!occupancy) {
                    break;
                }
            }
            if (!occupancy) {
                break;
            }
            test_p = node.position; // start back at the beginning
        }
        node.position = test_p;
    }
    
    calcPositionForNode=(node,d,occ_map,doc,allowDefault)=>{
        // find a reasonable position for this node

        if (!node.position) {
            // see if any of our prerequisites have a position
            let right_pos = this.findRightMostPrereqPosition(node,doc);
            if (right_pos) {
                node.position = {
                    x: right_pos.x + d.x + horizontal_spacing,
                    y: right_pos.y,
                };
            }
            else {
                console.log(`no right position found for ${node.name}`);
            }
        }
        
        if (!node.position) {
            // see if any nodes we are a prereqisute of have a position
            let left_pos = this.findLeftMostChildPosition(node,doc);
            if (left_pos) {
                node.position = {
                    x: left_pos.x - d.x - horizontal_spacing,
                    y: left_pos.y,
                };
            }
            else {
                console.log(`no left position found for ${node.name}`);
            }
        }
        
        if (!node.position) {
            // see if we can find any siblings (in same prereq list)
            let low_pos = this.findLowestSiblingPosition(node,doc);
            if (low_pos) {
                node.position = {
                    x: low_pos.x,
                    y: low_pos.y + d.y + vertical_spacing
                };
            }
            else {
                console.log(`no sibling position found for ${node.name}`);
            }
        }
        
        if (!node.position && allowDefault) {
            // default position
            console.log(`using default position for ${node.name}`);
            node.position = {
                x: Math.floor(graph_width / 2),
                y: Math.floor(graph_height / 2),
            };
            let nt = ShortCourseEditorStore.determineNodeType(node);
            if (nt === ShortCourseConstants.NODE_TYPES.CompletionNode) {
                node.position.x = graph_width - d.x - horizontal_spacing;
            }
        }
        // move the node elsewhere if it's position is overly occupied
        if (node.position) {
            this.ensureNodeIsInClearSpot(node,d,occ_map);
        }
    }
    
    getNodeDefaultName=(node)=>{
        switch(ShortCourseEditorStore.determineNodeType(node)) {
            case ShortCourseConstants.NODE_TYPES.CompletionNode:
                return 'Completion Node';
            case ShortCourseConstants.NODE_TYPES.SkillNode:
                return 'Skill Node';
        }
        return 'Node';
    }
    
    markNodePosition=(node,occ_map)=>{
        let p = node.position;
        let gx = Math.round(p.x / occ_grid_width);
        let gy = Math.round(p.y / occ_grid_height);
        let y_arr = occ_map[gy] || {};
        if (!y_arr[gx]) {
            y_arr[gx] = [node.node_id];
        }
        else {
            y_arr[gx] = y_arr[gx].concat(node.node_id);
        }
        occ_map[gy] = y_arr;
    }

    unmarkNodePosition=(node,occ_map,position)=>{
        let p = position;
        let gx = Math.round(p.x / occ_grid_width);
        let gy = Math.round(p.y / occ_grid_height);
        let y_arr = occ_map[gy] || {};
        if (!y_arr[gx]) {
            return false;
        }
        if (y_arr[gx].indexOf(node.node_id) < 0) {
            return false;
        }
        y_arr[gx] = _.without(y_arr[gx],node.node_id);
        occ_map[gy] = y_arr;
        return true;
    }

    
    getHandleSortedPrerequisites=(node,doc)=>{
        let preNodes = _.map(node.prereq_node_ids,(node_id)=>(_.find(doc.nodes,{node_id})));
        preNodes = _.filter(preNodes,(elem)=>(!!elem));
        preNodes = _.sortBy(preNodes,[(sortNode)=>(sortNode.position.x)]);
        return preNodes;
    }
    
    // return a map with the number of direct connections associated with each edge
    countEdges=(doc)=>{
        let edgeCount = {};
        let leftMap = {};
        let rightMap = {};
        _.forEach(doc.nodes,(node)=>{
            _.forEach(node.prereq_node_ids,(node_id)=>{
                let preNode = _.find(doc.nodes,{node_id});
                if (preNode) {
                    edgeCount[node_id] = (edgeCount[node_id] || 0) + 1; 
                    edgeCount[node.node_id] = (edgeCount[node.node_id] || 0) + 1; 
                    
                    // prerequisites of a node are considered to be to the left of them (and them to the right of thier prereqs)
                    rightMap[node_id] = (rightMap[node_id] || []).concat(node); 
                    leftMap[node.node_id] = (leftMap[node.node_id] || []).concat(preNode); 
                }
            });
        });
        return { edgeCount, leftMap, rightMap };
    }
    
    // returns { steps, endNode } in longest path along relation map (recursive) 
    // steps is set to MAX_SAFE_INTEGER if we found a cycle.
    findLongestPath=(node, relationMap, stopAtKnownPosition, recursionMap, startingValue)=>{
        if (!recursionMap) {
            recursionMap = {};
            startingValue = 0;
        }
        if (recursionMap[node.node_id]) {
            return { steps: Math.MAX_SAFE_INTEGER, endNode: null}; // cycle
        }
        recursionMap[node.node_id] = true;
        if ( stopAtKnownPosition && node.position) {
            return { steps: startingValue, endNode: node }; // found end
        }
        
        let arr = relationMap[node.node_id];
        if (!arr) {
            if (stopAtKnownPosition) {
                return { steps: startingValue, endNode: null };
            }
            return { steps: startingValue, endNode: node };
        }
        
        let chosenNode =  null;
        let chosen_steps = 0;
        let chosenRecursionMap = null;
        let chosenEnd = null;
        for (var i=0; i<arr.length; ++i) {
            let nextNode = arr[i];
            let recursionMapCopy = _.clone(recursionMap);
            let next_steps;
            let { steps, endNode } = this.findLongestPath(nextNode, relationMap, stopAtKnownPosition, recursionMapCopy, startingValue + 1);
            if (steps === Math.MAX_SAFE_INTEGER) {
                return { steps, endNode }; // cycle (we know there is no answer)
            }
            let better = false;
            if (!chosenNode) {
                better = true;
            }
            else if (endNode) {
                if (!chosenEnd) {
                    better = true;
                }
                else if (steps > chosen_steps) {
                    better = true;
                }
            }
            if (better) {
                chosen_steps = steps;
                chosenNode = nextNode;
                chosenEnd = endNode;
                chosenRecursionMap = recursionMapCopy;
            }
        }
        if (chosenNode) {
            recursionMap = chosenRecursionMap; // TODO what is this for?
            return { steps: chosen_steps, endNode: chosenEnd };
        }
         // dead end
        return { steps: startingValue, endNode: null };
    } // findLongestPath

    findBestNodeToPosition=(doc,edgeCount,leftMap,rightMap)=>{
        let bestNode = null;
        let best_left_distance,best_right_distance,best_nc;
        let bestLeft,bestRight;
        _.forEach(doc.nodes,(node)=>{
            if (node.position) {
                return;
            }
            
            let nc = edgeCount[node.node_id] || 0;
            let {steps: left_distance, endNode: leftEnd} = this.findLongestPath(node, leftMap, true);
            let {steps: right_distance, endNode: rightEnd} = this.findLongestPath(node, rightMap, true);
            
            //console.log(`${node.name} nc=${nc} left_distance=${left_distance} to ${leftEnd && leftEnd.name}, right_distance=${right_distance} to ${rightEnd && rightEnd.name}`);
            
            let better = (bestNode == null);
            if (!better) {
                let left_adj = (leftEnd && (left_distance === 1));
                let right_adj = (rightEnd && (right_distance === 1));
                let best_left_adj = (bestLeft && (best_left_distance === 1));
                let best_right_adj = (bestRight && (best_right_distance === 1));
                let has_adj = (left_adj || right_adj);
                let best_has_adj = (best_left_adj || best_right_adj);
                if (has_adj && !best_has_adj) {
                    better = true; // we are adjacent to a node which has a position
                }
                else if (!has_adj && !best_has_adj) {
                    let nt = ShortCourseEditorStore.determineNodeType(node);
                    if (nt === ShortCourseConstants.NODE_TYPES.CompletionNode) {
                        better = true;
                    }
                }
                else if (has_adj) {
                    if (right_adj && !best_right_adj) {
                        better = true;
                    }
                    else {
                        let worst_distance = Math.max(left_distance, right_distance);
                        let best_worst_distance = Math.max(best_left_distance, best_right_distance);
                        if (worst_distance > best_worst_distance) {
                            better = true;
                        }
                        else if (worst_distance === best_worst_distance) {
                            let nt = ShortCourseEditorStore.determineNodeType(node);
                            if (nt === ShortCourseConstants.NODE_TYPES.CompletionNode) {
                                better = true;
                            }
                        }
                    }
                }
            }
            if (better) {
                bestNode = node;
                best_left_distance = left_distance;
                best_right_distance = right_distance;
                bestLeft = leftEnd;
                bestRight = rightEnd;
                best_nc = nc;
            }
        });
        return bestNode;
    }
    
    buildElements=(doc)=>{
        var elements = [];
        var occ_map = {};
        if (doc && doc.nodes) {
            
            // fill in any missing positions 
            if (doc.nodes.length) {
                // mark what we know
                _.forEach(doc.nodes,(node)=>{
                    if (node.position) {
                        this.markNodePosition(node,occ_map);
                    }
                });
                
                let {edgeCount, leftMap, rightMap} = this.countEdges(doc);
                let passCount = 0;
                let maxPassCount = doc.nodes.length;
                
                // determine node positions
                do {
                    let bestNode = this.findBestNodeToPosition(doc,edgeCount,leftMap,rightMap);
                    if (!bestNode) {
                        break; // should be done
                    }
                    console.log(`determining position for ${bestNode.name}`);
                    passCount++;
                    let d = getNodeDimensions(bestNode);
                    this.calcPositionForNode(bestNode,d,occ_map,doc,true);
                    if (!bestNode.position) {
                        console.log("no default node position?",bestNode);
                    }
                    else {
                        this.markNodePosition(bestNode,occ_map);
                    }
                } while(passCount < maxPassCount);
            } // scope for determining node positions
            
            _.forEach(doc.nodes,(node)=>{
                let preNodes = this.getHandleSortedPrerequisites(node,doc);
                // add edges to prerequisite nodes
                _.forEach(preNodes,(sourceNode,pre_index)=>{
                    let edge_id = `e:${sourceNode.node_id}:${node.node_id}`
                    elements.push({
                        id: edge_id,
                        source: sourceNode.node_id,
                        target: node.node_id,
                        targetHandle: `p${pre_index+1}`,
                        type: 'straight',
                        arrowHeadType,
                    });
                });
                let data = {
                    name: node.name || this.getNodeDefaultName(node),
                    description: node.description,
                    numTargetHandles: preNodes.length,
                    img_src: node.img_src && AssetUtil.resolveAssetPath(node.img_src),
                };
                elements.push({
                    id: node.node_id,
                    type: ShortCourseEditorStore.determineNodeType(node),
                    position: node.position, // if undefined we need to calculate a position
                    selectable: true,
                    connectable: true,
                    sourcePosition: 'bottom', // 'right',
                    targetPosition: 'top', // 'left',
                    data
                });
                
                if (preNodes.length < node.prereq_node_ids.length) {
                    console.log(`removing invalid prereq(s) from node: ${JSON.stringify({node_id:node.node_id, prereq_node_ids:node.prereq_node_ids})}`);
                    node.prereq_node_ids = _.map(preNodes,'node_id');
                }
            });
        }
        return { elements, occ_map };
    }
    
    onLoad=(flowInst)=>{
        if (flowInst && flowInst.fitView) {
            setTimeout(()=>{
                flowInst.fitView({padding:1});
            },1);
        }
    }

    render() {
        return (
            <div className="short-course-graph">
                <ReactFlowProvider>
                    <ReactFlow nodesConnectable={true} nodeTypes={nodeTypes} onLoad={this.onLoad}
                        arrowHeadColor={arrowHeadColor}
                        onConnect={(params)=>{
                            let elements = this.state.elements;
                            let id = `e:${params.source}:${params.target}`;
                            if (_.find(elements,{id})) {
                                console.log(`edge ${id} already exists`);
                                return;
                            }
                            if (params.source === params.target) {
                                console.log("no self-connect");
                                return;
                            }
                            ShortCourseEditorActions.addPrereq(uuid.v4(),params.target,params.source);
                        }}
                            
                        onEdgeUpdate={(edge,params)=>{
                            let old_source = edge.source;
                            let old_target = edge.target;
                            ShortCourseEditorActions.removePrereq(uuid.v4(),old_target,old_source);
                            ShortCourseEditorActions.addPrereq(uuid.v4(),params.target,params.source);
                        }}
                        
                        onNodeDragStop={(evt,elem)=>{
                            ShortCourseEditorActions.setNodePosition(uuid.v4(),elem.id, elem.position);
                        }}
                        
                        onPaneClick={()=>{
                            ShortCourseEditorActions.selectNode(uuid.v4(),null); // deselect
                        }}
                        
                        onSelectionChange={(elems)=>{
                            if (elems && (elems.length < 2)) {
                                let elem = elems[0]; // could be undefined (deselect)
                                ShortCourseEditorActions.selectNode(uuid.v4(),elem && elem.id);
                            }
                            else {
                                //console.log(`onSelectionChange multi-select: ${_.map(elems,'id')}`); // not sure if this can happen
                            }
                        }}
                        elements={this.state.elements}
                        />
                </ReactFlowProvider>
            </div>
    	);
    }
}

ShortCourseFlowDiagram.propTypes = propTypes;
ShortCourseFlowDiagram.defaultProps = defaultProps;


export default ShortCourseFlowDiagram;